import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, X, MapPin, Plane } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { city: "", country: "", flag: "", date: "", end_date: "", label: "shoot", notes: "", sort_order: 0 };
const LABELS = ["base", "shoot", "show", "meet", "vacation", "return", "other"];

export default function Tours() {
  const [stops, setStops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => setStops((await api.get("/api/tours/")).data);
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() =>
    [...stops].sort((a, b) => (a.sort_order - b.sort_order) || a.date.localeCompare(b.date))
  , [stops]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = sorted.filter(s => s.date >= today);
  const past = sorted.filter(s => s.date < today);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, sort_order: stops.length }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.city || !form.date) return toast.error("City and date required");
    const body = { ...form, sort_order: parseInt(form.sort_order) || 0 };
    try {
      if (editing) await api.put(`/api/tours/${editing.id}`, body);
      else await api.post("/api/tours/", body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tour stop?")) return;
    await api.delete(`/api/tours/${id}`); load();
  };

  return (
    <AppLayout
      title="Tours · Travel"
      overline="060 · ALL CITIES · UPCOMING + PAST"
      actions={
        <button onClick={openNew} className="btn-primary inline-flex items-center gap-2" data-testid="tour-new-button">
          <Plus size={15} /> Add stop
        </button>
      }
    >
      {/* Visual timeline */}
      {sorted.length > 0 && (
        <div className="bg-white border rounded-lg p-6 mb-6 fade-up" style={{ borderColor: "var(--gio-border)", boxShadow: "var(--gio-shadow-sm)" }} data-testid="tour-visual">
          <div className="text-[12px] font-bold uppercase tracking-wider mb-4" style={{ color: "var(--gio-primary)" }}>FULL TIMELINE</div>
          <div className="relative">
            <div className="absolute left-0 right-0 top-7 h-0.5" style={{ background: "var(--gio-border)" }} />
            <div className="relative grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(sorted.length, 1)}, minmax(80px, 1fr))` }}>
              {sorted.map((s) => {
                const isUpcoming = s.date >= today;
                const isNext = upcoming[0]?.id === s.id;
                return (
                  <div key={s.id} className="flex flex-col items-center text-center cursor-pointer" onClick={() => openEdit(s)} data-testid={`tour-mini-${s.id}`}>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-2 transition" style={{
                      background: isNext ? "var(--gio-primary-soft)" : isUpcoming ? "var(--gio-surface)" : "var(--gio-surface-2)",
                      border: `2px solid ${isNext ? "var(--gio-primary)" : isUpcoming ? "var(--gio-border)" : "var(--gio-border)"}`,
                      boxShadow: isNext ? "0 0 0 4px rgba(27,150,255,0.15)" : "none",
                      opacity: isUpcoming ? 1 : 0.55,
                    }}>
                      {s.flag || "📍"}
                    </div>
                    <div className="text-[13px] font-semibold" style={{ color: isNext ? "var(--gio-primary)" : "var(--gio-text)" }}>{s.city}</div>
                    <div className="text-[11px] font-mono-ui" style={{ color: "var(--gio-muted-2)" }}>{s.date.slice(5)}</div>
                    {s.label && <div className="text-[10px] font-bold uppercase mt-0.5" style={{ color: "var(--gio-muted-2)" }}>{s.label}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Upcoming list */}
      <SectionTitle title={`Upcoming · ${upcoming.length}`} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="tour-upcoming">
        {upcoming.map((s) => <StopCard key={s.id} s={s} onEdit={openEdit} onDelete={remove} highlight />)}
        {upcoming.length === 0 && <Empty>No upcoming stops yet.</Empty>}
      </div>

      <SectionTitle title={`Past · ${past.length}`} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tour-past">
        {past.map((s) => <StopCard key={s.id} s={s} onEdit={openEdit} onDelete={remove} />)}
        {past.length === 0 && <Empty>—</Empty>}
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="tour-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "var(--gio-shadow-lg)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[13px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-primary)" }}>
                {editing ? "EDIT" : "NEW"} · TOUR STOP
              </div>
              <button onClick={() => setOpen(false)} data-testid="tour-modal-close"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field l="City *"><input data-testid="tour-f-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="gio-input" /></Field>
              <Field l="Country"><input data-testid="tour-f-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="gio-input" /></Field>
              <Field l="Flag emoji"><input data-testid="tour-f-flag" value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="🇮🇹" className="gio-input" /></Field>
              <Field l="Label">
                <select value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="gio-input" data-testid="tour-f-label">
                  {LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field l="Date *"><input data-testid="tour-f-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="gio-input" /></Field>
              <Field l="End date"><input data-testid="tour-f-enddate" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="gio-input" /></Field>
              <Field l="Sort order"><input data-testid="tour-f-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="gio-input" /></Field>
            </div>
            <Field l="Notes"><textarea data-testid="tour-f-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gio-input" /></Field>
            <div className="flex gap-3 mt-4">
              <button onClick={save} className="btn-primary flex-1" data-testid="tour-save">Save</button>
              <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="tour-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function StopCard({ s, onEdit, onDelete, highlight }) {
  return (
    <div className="bg-white border rounded-lg p-5 group transition hover:shadow-md" style={{ borderColor: "var(--gio-border)", boxShadow: "var(--gio-shadow-sm)" }} data-testid={`tour-card-${s.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{s.flag || "📍"}</div>
          <div>
            <div className="font-heading text-xl" style={{ color: "var(--gio-text)" }}>{s.city}</div>
            <div className="text-[12px]" style={{ color: "var(--gio-muted-2)" }}>{s.country}</div>
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <button onClick={() => onEdit(s)} data-testid={`tour-edit-${s.id}`}><Pencil size={15} style={{ color: "var(--gio-muted)" }} /></button>
          <button onClick={() => onDelete(s.id)} data-testid={`tour-delete-${s.id}`}><Trash2 size={15} style={{ color: "var(--gio-muted)" }} /></button>
        </div>
      </div>
      <div className="flex items-center gap-3 text-[13px] mb-2">
        <span className="font-mono-ui" style={{ color: highlight ? "var(--gio-primary)" : "var(--gio-muted)" }}>
          <Plane size={13} className="inline mr-1" />{s.date}{s.end_date && ` → ${s.end_date}`}
        </span>
        {s.label && (
          <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
            {s.label}
          </span>
        )}
      </div>
      {s.notes && <div className="text-[13px]" style={{ color: "var(--gio-muted)" }}>{s.notes}</div>}
    </div>
  );
}

function SectionTitle({ title }) {
  return <h2 className="font-heading text-xl mb-3 mt-2" style={{ color: "var(--gio-text)" }}>{title}</h2>;
}

function Field({ l, children }) {
  return (
    <div className="mb-3">
      <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>{l}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="col-span-full text-center py-8 text-[14px]" style={{ color: "var(--gio-muted-3)" }}>{children}</div>;
}
